// frontend/src/App.jsx
import React, { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { WebSocketProvider } from './contexts/WebSocketContext'
import { BrowserSessionProvider } from './contexts/BrowserSessionContext'
import { initializeVideoStore } from './stores/videoStore'
import Layout from './components/common/Layout'
import ProtectedRoute from './components/auth/ProtectedRoute'

// Pages
import TestCrewLanding from './pages/TestCrewLanding'
import Home from './pages/Home'
import Login from './pages/Login'
import Register from './pages/Register'
import AutomationMode from './pages/AutomationMode'
import ConversationalMode from './pages/ConversationalMode'
import TestHistory from './pages/TestHistory'
import Analytics from './pages/Analytics'
import Settings from './pages/Settings'
import FigmaComparison from './pages/FigmaComparison'


// Component to handle root route logic
const RootRoute = () => {
  const { isAuthenticated, loading } = useAuth()
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    )
  }
  
  // If authenticated, redirect to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }
  
  // If not authenticated, show landing page
  return <TestCrewLanding />
}

function App() {
  // Initialize video store on app mount
  useEffect(() => {
    try {
      initializeVideoStore()
    } catch (error) {
      console.error('Failed to initialize video store:', error)
    }
  }, [])

  return (
    <AuthProvider>
      <WebSocketProvider>
        <BrowserSessionProvider>
          <Router>
            <div className="App">
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<RootRoute />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                
                {/* Protected routes */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Layout>
                      <Home />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/automation" element={
                  <ProtectedRoute>
                    <Layout>
                      <AutomationMode />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/figma-comparison" element={
                  <ProtectedRoute>
                    <Layout>
                      <FigmaComparison />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/conversational" element={
                  <ProtectedRoute>
                    <Layout>
                      <ConversationalMode />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/history" element={
                  <ProtectedRoute>
                    <Layout>
                      <TestHistory />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/analytics" element={
                  <ProtectedRoute>
                    <Layout>
                      <Analytics />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <Layout>
                      <Settings />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                {/* Catch all route */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              
              <Toaster 
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                  },
                }}
              />
            </div>
          </Router>
        </BrowserSessionProvider>
      </WebSocketProvider>
    </AuthProvider>
  )
}

export default App